(() => {
  const LAZY_SELECTOR = [
    'img[data-home-lazy-src]',
    'video[data-home-lazy-video]',
    'iframe[data-home-lazy-src]'
  ].join(',');
  const ROOT_MARGIN = '300px 0px';
  const transparentPixel = 'data:image/gif;base64,R0lGODlhAQABAAAAACw=';
  const observed = new WeakSet();
  const loaded = new WeakSet();
  let observer = null;

  const isVisible = (element) => {
    if (!element || !element.isConnected) return false;
    if (element.hidden) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    const rects = element.getClientRects();
    return rects.length > 0;
  };

  const isNearViewport = (element) => {
    if (!isVisible(element)) return false;

    const rect = element.getBoundingClientRect();
    const margin = 300;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return rect.bottom >= -margin
      && rect.top <= viewportHeight + margin
      && rect.right >= -margin
      && rect.left <= viewportWidth + margin;
  };

  const hydratePictureSources = (image) => {
    const picture = image.closest('picture');
    if (!picture) return;

    picture.querySelectorAll('source[data-home-lazy-srcset]').forEach((source) => {
      source.setAttribute('srcset', source.dataset.homeLazySrcset);
      source.removeAttribute('data-home-lazy-srcset');
    });
  };

  const hydrateImage = (image) => {
    hydratePictureSources(image);

    if (image.dataset.homeLazySrcset) {
      image.setAttribute('srcset', image.dataset.homeLazySrcset);
      image.removeAttribute('data-home-lazy-srcset');
    }

    if (image.dataset.homeLazySizes) {
      image.setAttribute('sizes', image.dataset.homeLazySizes);
      image.removeAttribute('data-home-lazy-sizes');
    }

    if (image.dataset.homeLazySrc) {
      image.setAttribute('src', image.dataset.homeLazySrc);
      image.removeAttribute('data-home-lazy-src');
    }

    loaded.add(image);
  };

  const hydrateVideo = (video) => {
    if (video.dataset.homeLazyPoster) {
      video.setAttribute('poster', video.dataset.homeLazyPoster);
      video.removeAttribute('data-home-lazy-poster');
    }

    video.querySelectorAll('source[data-home-lazy-src]').forEach((source) => {
      source.setAttribute('src', source.dataset.homeLazySrc);
      source.removeAttribute('data-home-lazy-src');
    });

    video.preload = video.dataset.homeLazyPreload || 'metadata';
    video.removeAttribute('data-home-lazy-video');
    video.load();

    if (video.autoplay) {
      const playRequest = video.play();
      if (playRequest && typeof playRequest.catch === 'function') {
        playRequest.catch(() => {});
      }
    }

    loaded.add(video);
  };

  const hydrateIframe = (iframe) => {
    iframe.setAttribute('src', iframe.dataset.homeLazySrc);
    iframe.removeAttribute('data-home-lazy-src');
    loaded.add(iframe);
  };

  const hydrate = (element, options = {}) => {
    if (!element || loaded.has(element)) return false;
    if (!options.force && !isNearViewport(element)) return false;

    if (element.matches('img')) {
      hydrateImage(element);
      return true;
    }

    if (element.matches('video')) {
      hydrateVideo(element);
      return true;
    }

    if (element.matches('iframe')) {
      hydrateIframe(element);
      return true;
    }

    return false;
  };

  const observe = (element) => {
    if (!element || observed.has(element)) return;

    if (!element.getAttribute('src') && element.matches('img[data-home-lazy-src]')) {
      element.setAttribute('src', transparentPixel);
    }

    observed.add(element);

    if (!observer) {
      hydrate(element);
      return;
    }

    observer.observe(element);
  };

  const refresh = (root = document) => {
    root.querySelectorAll(LAZY_SELECTOR).forEach(observe);
  };

  const loadWithin = (root, options = {}) => {
    if (!root) return;
    root.querySelectorAll(LAZY_SELECTOR).forEach((element) => hydrate(element, options));
  };

  const init = () => {
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          hydrate(entry.target, { force: true });
          observer.unobserve(entry.target);
        });
      }, { rootMargin: ROOT_MARGIN });
    }

    refresh();
  };

  window.MondoHomeLazyMedia = {
    refresh,
    loadWithin,
    isNearViewport
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
  });
})();
